import { ArrowDownRight, ArrowUpRight, ChevronLeft, ChevronRight, type LucideIcon } from 'lucide-react'
import { motion } from 'motion/react'
import type { ComponentProps, ReactNode } from 'react'
import type { PageMeta } from '../lib/types'
import { cn } from '../lib/utils'
import { Button } from './button'
import { Card, Skeleton } from './primitives'

/* --------------------------------------------------------------- Table */

export function TableContainer({ className, ...props }: ComponentProps<'div'>) {
  return (
    <div
      className={cn('overflow-hidden rounded-xl border border-border bg-surface shadow-soft', className)}
      {...props}
    />
  )
}

export function Table({ className, ...props }: ComponentProps<'table'>) {
  return (
    <div className="w-full scrollbar-thin overflow-x-auto">
      <table className={cn('w-full caption-bottom border-collapse text-sm', className)} {...props} />
    </div>
  )
}

export function TableHeader({ className, ...props }: ComponentProps<'thead'>) {
  return <thead className={cn('bg-surface-2/70 [&_tr]:border-b', className)} {...props} />
}

export function TableBody({ className, ...props }: ComponentProps<'tbody'>) {
  return <tbody className={cn('[&_tr:last-child]:border-0', className)} {...props} />
}

export function TableRow({ className, ...props }: ComponentProps<'tr'>) {
  return (
    <tr
      className={cn(
        'border-b border-border transition-colors hover:bg-surface-2/60 data-[state=selected]:bg-accent',
        className,
      )}
      {...props}
    />
  )
}

export function TableHead({ className, ...props }: ComponentProps<'th'>) {
  return (
    <th
      className={cn(
        'h-10 px-4 text-left align-middle text-xs font-medium tracking-wide whitespace-nowrap text-muted-foreground uppercase',
        className,
      )}
      {...props}
    />
  )
}

export function TableCell({ className, ...props }: ComponentProps<'td'>) {
  return <td className={cn('px-4 py-3 align-middle', className)} {...props} />
}

export function TableSkeleton({ rows = 6, columns = 4 }: { rows?: number; columns?: number }) {
  return (
    <div className="divide-y divide-border" aria-label="Loading" role="status">
      {Array.from({ length: rows }, (_, row) => (
        <div key={row} className="flex items-center gap-4 px-4 py-3.5">
          <Skeleton className="size-8 rounded-full" />
          {Array.from({ length: columns }, (_, col) => (
            <Skeleton key={col} className={cn('h-3.5', col === 0 ? 'w-40' : 'hidden w-24 flex-1 sm:block')} />
          ))}
        </div>
      ))}
    </div>
  )
}

export function Pagination({ meta, onPage }: { meta: PageMeta; onPage: (page: number) => void }) {
  if (meta.total === 0) return null
  const from = (meta.current_page - 1) * meta.per_page + 1
  const to = Math.min(meta.current_page * meta.per_page, meta.total)

  return (
    <div className="flex items-center justify-between gap-3 border-t border-border px-4 py-3 text-sm">
      <p className="text-muted-foreground">
        <span className="tabular font-medium text-foreground">
          {from}–{to}
        </span>{' '}
        of <span className="tabular font-medium text-foreground">{meta.total}</span>
      </p>
      <div className="flex items-center gap-1">
        <Button
          variant="secondary"
          size="icon-sm"
          aria-label="Previous page"
          disabled={meta.current_page <= 1}
          onClick={() => onPage(meta.current_page - 1)}
        >
          <ChevronLeft />
        </Button>
        <span className="tabular px-2 text-muted-foreground">
          {meta.current_page} / {meta.last_page}
        </span>
        <Button
          variant="secondary"
          size="icon-sm"
          aria-label="Next page"
          disabled={meta.current_page >= meta.last_page}
          onClick={() => onPage(meta.current_page + 1)}
        >
          <ChevronRight />
        </Button>
      </div>
    </div>
  )
}

/* ---------------------------------------------------------- EmptyState */

export function EmptyState({
  icon: Icon,
  title,
  description,
  action,
  className,
}: {
  icon: LucideIcon
  title: string
  description?: ReactNode
  action?: ReactNode
  className?: string
}) {
  return (
    <div className={cn('flex flex-col items-center justify-center px-6 py-16 text-center', className)}>
      <div className="relative mb-5">
        <div className="absolute inset-0 -z-10 scale-150 rounded-full bg-brand-500/10 blur-2xl" aria-hidden />
        <div className="grid size-12 place-items-center rounded-2xl border border-border bg-surface shadow-elevated">
          <Icon className="size-5 text-primary" />
        </div>
      </div>
      <h3 className="text-[15px] font-semibold tracking-tight">{title}</h3>
      {description && <p className="mt-1.5 max-w-sm text-sm text-muted-foreground">{description}</p>}
      {action && <div className="mt-5">{action}</div>}
    </div>
  )
}

/* ------------------------------------------------------------- KpiCard */

export function KpiCard({
  label,
  value,
  icon: Icon,
  hint,
  trend,
  loading = false,
  index = 0,
}: {
  label: string
  value: ReactNode
  icon: LucideIcon
  hint?: ReactNode
  /** Percent change versus the previous period; omitted when unknown. */
  trend?: number | undefined
  loading?: boolean
  index?: number
}) {
  const up = (trend ?? 0) >= 0

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: index * 0.05, ease: [0.16, 1, 0.3, 1] }}
    >
      <Card className="group relative overflow-hidden p-5 transition-shadow hover:shadow-elevated">
        <div
          className="pointer-events-none absolute -top-10 -right-10 size-32 rounded-full bg-brand-500/8 blur-2xl transition-opacity group-hover:opacity-100"
          aria-hidden
        />
        <div className="flex items-start justify-between gap-3">
          <p className="text-[13px] font-medium text-muted-foreground">{label}</p>
          <div className="grid size-8 place-items-center rounded-lg bg-accent text-accent-foreground">
            <Icon className="size-4" />
          </div>
        </div>
        {loading ? (
          <Skeleton className="mt-3 h-8 w-32" />
        ) : (
          <p className="tabular mt-2 text-2xl font-semibold tracking-tight">{value}</p>
        )}
        <div className="mt-2 flex items-center gap-2 text-xs text-muted-foreground">
          {trend !== undefined && (
            <span
              className={cn(
                'inline-flex items-center gap-0.5 font-medium',
                up ? 'text-success' : 'text-danger',
              )}
            >
              {up ? <ArrowUpRight className="size-3.5" /> : <ArrowDownRight className="size-3.5" />}
              {Math.abs(trend).toFixed(1)}%
            </span>
          )}
          {hint}
        </div>
      </Card>
    </motion.div>
  )
}

/* ---------------------------------------------------------- PageHeader */

export function PageHeader({
  title,
  description,
  actions,
  eyebrow,
}: {
  title: string
  description?: ReactNode
  actions?: ReactNode
  eyebrow?: ReactNode
}) {
  return (
    <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
      <div className="min-w-0">
        {eyebrow && <div className="mb-1.5 text-xs font-medium text-primary">{eyebrow}</div>}
        <h1 className="text-2xl font-semibold tracking-tight text-balance">{title}</h1>
        {description && <p className="mt-1 text-sm text-muted-foreground">{description}</p>}
      </div>
      {actions && <div className="flex shrink-0 flex-wrap items-center gap-2">{actions}</div>}
    </div>
  )
}
